/**
 * Live-Aktualisierung: das Backend meldet über Server-Sent Events, in welchem
 * Bereich sich etwas geändert hat (GET /live-updates/stream, Ereignis
 * "change" mit { resource }). Diese Datei übersetzt den Bereich in die
 * betroffenen react-query-Abfragen — ohne React, damit es testbar bleibt.
 */

/**
 * Bereich der API → Stichworte. Eine Abfrage ist betroffen, wenn der erste
 * Teil ihres queryKey (kleingeschrieben) eines der Stichworte enthält.
 * Über Stichworte statt fester Schlüssel, weil z. B. eine Rechnungsfreigabe
 * auch die Turnus-Liste, die Checkliste und die Klienten-Übersicht ändert.
 *
 * Agentur-Einstellungen fehlen bewusst: ihre Formulare übernehmen neu geladene
 * Daten sofort und würden dabei ungespeicherte Eingaben verlieren.
 */
const RESOURCE_KEYWORDS: Record<string, string[]> = {
  'manual-shifts': ['shift', 'casenumber', 'checklist', 'care-assignment'],
  shifts: ['shift', 'casenumber', 'checklist', 'care-assignment'],
  'care-assignments': ['care-assignment', 'shift'],
  invoices: ['invoice', 'shift'],
  'family-invoice-schedules': ['invoice'],
  'client-billing': ['invoice', 'shift'],
  patients: ['patient', 'client', 'shift'],
  'patient-profiles': ['patient', 'client'],
  'client-onboarding': ['patient', 'client', 'onboarding'],
  caregivers: ['caregiver', 'shift'],
  tasks: ['task'],
  knowledge: ['knowledge'],
  templates: ['template', 'emaillist'],
  'checklist-templates': ['checklist'],
  files: ['file', 'document'],
  matching: ['matching'],
  'email-triage': ['email-log', 'person-email-history'],
  users: ['staff'],
};

export const isKnownLiveResource = (resource: string): boolean => resource in RESOURCE_KEYWORDS;

/** Trifft eine der geänderten Ressourcen diese Abfrage? */
export const queryTouchedBy = (resources: readonly string[], queryKey: readonly unknown[]): boolean => {
  const head = typeof queryKey[0] === 'string' ? queryKey[0].toLowerCase() : '';
  if (!head) return false;
  for (const resource of resources) {
    if ((RESOURCE_KEYWORDS[resource] || []).some((keyword) => head.includes(keyword))) return true;
  }
  return false;
};

export interface SseMessage {
  event: string;
  data: string;
}

/**
 * Zerlegt den bisher empfangenen Text eines SSE-Stroms in vollständige
 * Nachrichten; `rest` ist der noch unvollständige Schluss für den nächsten
 * Aufruf.
 */
export const parseSse = (buffer: string): { messages: SseMessage[]; rest: string } => {
  const normalized = buffer.replace(/\r\n/g, '\n');
  const blocks = normalized.split('\n\n');
  const rest = blocks.pop() ?? '';
  const messages: SseMessage[] = [];
  for (const block of blocks) {
    let event = 'message';
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).trim());
    }
    if (data.length || event !== 'message') messages.push({ event, data: data.join('\n') });
  }
  return { messages, rest };
};
